import React, { useState, useMemo, useCallback } from "react";
import { fetchWrapper } from "utils/apiFetch";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";

// ─── helpers ──────────────────────────────────────────────────────────────────
const inr = (n) =>
  "₹ " + (parseFloat(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getTodayRange = () => {
  const t = new Date().toISOString().split("T")[0];
  return { start: t, end: t };
};

const buildUrl = (userid, start, end, memberId = "") => {
  let url = `https://api.mokshiq.in/fund_summary_report?user=${userid}&filter_date=${start}&end_date=${end}`;
  if (memberId) url += `&member_id=${memberId}`;
  return url;
};

const FLOW_ICONS = {
  credit: "ph-arrow-circle-down",
  debit:  "ph-arrow-circle-up",
};
const FLOW_COLORS = {
  credit: "text-success",
  debit:  "text-danger",
};
const FLOW_BG = {
  credit: "text-light-success",
  debit:  "text-light-danger",
};

// ─── summary stat card (matches MemberSettingManagement wallet cards) ─────────
function StatCard({ label, value, icon, colorClass, bgClass }) {
  return (
    <div className={`card hover-effect ${bgClass} mb-0`}>
      <div className="card-body d-flex align-items-center justify-content-between">
        <div>
          <p className="mb-1 text-secondary f-s-13 f-w-500">{label}</p>
          <h4 className={`mb-0 f-w-700 ${colorClass}`}>{value}</h4>
        </div>
        <span className={`h-50 w-50 d-flex-center b-r-50 ${colorClass.replace("text-", "bg-")}`}>
          <i className={`ph-duotone ${icon} f-s-26 text-white`}></i>
        </span>
      </div>
    </div>
  );
}

// ─── expandable day-wise member row ──────────────────────────────────────────
function DayRow({ row, isOpen, onToggle }) {
  const net = (row.total_credit || 0) - (row.total_debit || 0);
  return (
    <>
      <tr
        className="cursor-pointer"
        onClick={onToggle}
        style={{ cursor: "pointer" }}
      >
        <td>
          <span className="badge text-light-primary f-s-12 px-2">
            {row.txn_date}
          </span>
        </td>
        <td>
          <div className="f-w-600 f-s-14">{row.member_name || "—"}</div>
          <div className="text-secondary f-s-12">{row.member_phone}</div>
        </td>
        <td>
          <span className="badge text-light-secondary f-s-11">{row.member_role}</span>
        </td>
        <td className="f-w-600 text-secondary">{inr(row.opening_balance)}</td>
        <td>
          <span className="text-success f-w-700">
            <i className="ph-fill ph-arrow-circle-down me-1"></i>
            {inr(row.total_credit)}
          </span>
          <div className="text-secondary f-s-11">{row.credit_count} txn{row.credit_count !== 1 ? "s" : ""}</div>
        </td>
        <td>
          <span className="text-danger f-w-700">
            <i className="ph-fill ph-arrow-circle-up me-1"></i>
            {inr(row.total_debit)}
          </span>
          <div className="text-secondary f-s-11">{row.debit_count} txn{row.debit_count !== 1 ? "s" : ""}</div>
        </td>
        <td>
          <span className={`f-w-700 ${net >= 0 ? "text-success" : "text-danger"}`}>
            {net >= 0 ? "+" : ""}{inr(net)}
          </span>
        </td>
        <td className="f-w-600 text-primary">{inr(row.closing_balance)}</td>
        <td>
          <i className={`ph-bold ${isOpen ? "ph-caret-up" : "ph-caret-down"} text-secondary`}></i>
        </td>
      </tr>

      {/* Expanded transaction details */}
      {isOpen && (
        <tr>
          <td colSpan={9} style={{ padding: 0, background: "#f8f9fc" }}>
            <div className="p-3">
              <div className="row g-2">
                {(row.transactions || []).map((t, i) => (
                  <div className="col-md-6 col-xl-4" key={i}>
                    <div className="card mb-0" style={{ borderLeft: `3px solid ${t.flow === "credit" ? "#198754" : "#dc3545"}` }}>
                      <div className="card-body py-2 px-3">
                        <div className="d-flex align-items-start justify-content-between">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className={`badge ${t.flow === "credit" ? "text-light-success" : "text-light-danger"} f-s-11`}>
                                <i className={`ph-fill ${FLOW_ICONS[t.flow]} me-1`}></i>
                                {t.flow?.toUpperCase()}
                              </span>
                              <span className={`f-w-700 f-s-15 ${t.flow === "credit" ? "text-success" : "text-danger"}`}>
                                {inr(t.amount)}
                              </span>
                            </div>
                            <div className="f-s-12 text-secondary mb-1">{t.flow_label}</div>
                            {t.utr && (
                              <div className="d-flex align-items-center gap-1">
                                <i className="ph ph-hash f-s-12 text-secondary"></i>
                                <span className="f-s-12 f-w-500 text-primary">{t.utr}</span>
                              </div>
                            )}
                            {t.remark && (
                              <div className="f-s-11 text-secondary mt-1">
                                <i className="ph ph-note me-1"></i>{t.remark}
                              </div>
                            )}
                          </div>
                          <div className="text-end ms-2" style={{ minWidth: 90 }}>
                            <div className="f-s-11 text-secondary">{t.txn_datetime}</div>
                            <div className="f-s-11 text-secondary mt-1">
                              <span className="text-muted">Open: </span>{inr(t.opening_balance)}
                            </div>
                            <div className="f-s-11 f-w-600 text-primary mt-0">
                              <span className="text-muted">Close: </span>{inr(t.closing_balance)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
function FundSummaryReportPage() {
  const { userid, role } = useSession();
  const { start, end } = getTodayRange();

  const [fromDate, setFromDate]   = useState(start);
  const [toDate, setToDate]       = useState(end);
  const [memberId, setMemberId]   = useState("");
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [activeTab, setActiveTab] = useState("summary"); // "summary" | "detail" | "members"
  const [page, setPage]           = useState(1);
  const PAGE_SIZE = 50;

  const fetchData = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const url = buildUrl(userid, fromDate, toDate, memberId);
      const res = await fetchWrapper(url);
      if (res.status) {
        setData(res);
        setPage(pg);
      } else {
        showMainToast(res.error || "Failed to load data", "danger");
      }
    } catch (e) {
      showMainToast("Network error", "danger");
    } finally {
      setLoading(false);
    }
  }, [userid, fromDate, toDate, memberId]);

  const g = data?.grand_totals || {};
  const summary = data?.day_wise_summary || [];
  const members = data?.member_totals   || [];
  const detail  = data?.data            || [];
  const pagination = data?.pagination   || {};

  // Paginate detail client-side for the table
  const detailPage = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return detail.slice(start, start + PAGE_SIZE);
  }, [detail, page]);

  const exportCSV = () => {
    if (!detail.length) { showMainToast("No data to export", "warning"); return; }
    const keys = Object.keys(detail[0]);
    let csv = keys.join(",") + "\n";
    detail.forEach(r => {
      csv += keys.map(k => {
        const v = String(r[k] ?? "").replace(/"/g, '""');
        return v.includes(",") ? `"${v}"` : v;
      }).join(",") + "\n";
    });
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = `fund_summary_${fromDate}_to_${toDate}.csv`;
    a.click();
  };

  return (
    <main>
      <div className="container-fluid">

        {/* ── Breadcrumb ── */}
        <div className="row m-1">
          <div className="col-12 d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h4 className="main-title">Fund Summary Report</h4>
              <ul className="app-line-breadcrumbs mb-3">
                <li>
                  <a className="f-s-14 f-w-500" href="#">
                    <span className="d-flex align-items-center gap-1">
                      <i className="ph-duotone ph-chart-line-up f-s-16"></i>
                      Reports
                    </span>
                  </a>
                </li>
                <li className="active">
                  <span className="f-s-14 f-w-500">Fund Summary</span>
                </li>
              </ul>
            </div>
            <div className="mt-1">
              <button className="btn btn-outline-primary me-2" onClick={exportCSV}>
                <i className="ph-duotone ph-download-simple me-1"></i>
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* ── Filter card ── */}
        <div className="card mb-3">
          <div className="card-header">
            <h5><i className="ph-duotone ph-funnel me-2"></i>Filter</h5>
          </div>
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3 col-6">
                <label className="form-label f-s-13 f-w-500">From Date</label>
                <input
                  type="date" className="form-control"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                />
              </div>
              <div className="col-md-3 col-6">
                <label className="form-label f-s-13 f-w-500">To Date</label>
                <input
                  type="date" className="form-control"
                  value={toDate} min={fromDate}
                  onChange={e => setToDate(e.target.value)}
                />
              </div>
              <div className="col-md-3 col-6">
                <label className="form-label f-s-13 f-w-500">Member ID <span className="text-secondary">(optional)</span></label>
                <input
                  type="text" className="form-control"
                  placeholder="Filter single member"
                  value={memberId}
                  onChange={e => setMemberId(e.target.value)}
                />
              </div>
              <div className="col-md-3 col-6">
                <button
                  className="btn btn-primary w-100"
                  onClick={() => fetchData(1)}
                  disabled={loading}
                >
                  {loading
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Loading…</>
                    : <><i className="ph-bold ph-magnifying-glass me-2"></i>Fetch Report</>
                  }
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Grand totals strip ── */}
        {data && (
          <div className="row g-3 mb-3">
            <div className="col-md-3 col-6">
              <StatCard
                label="Total Credit"
                value={inr(g.total_credit)}
                icon="ph-arrow-circle-down"
                colorClass="text-success"
                bgClass="card-light-success"
              />
            </div>
            <div className="col-md-3 col-6">
              <StatCard
                label="Total Debit"
                value={inr(g.total_debit)}
                icon="ph-arrow-circle-up"
                colorClass="text-danger"
                bgClass="card-light-danger"
              />
            </div>
            <div className="col-md-3 col-6">
              <StatCard
                label="Total Transactions"
                value={g.total_txns?.toLocaleString("en-IN") || 0}
                icon="ph-receipt"
                colorClass="text-primary"
                bgClass="card-light-primary"
              />
            </div>
            <div className="col-md-3 col-6">
              <StatCard
                label="Members"
                value={`${g.member_count || 0} · ${g.day_count || 0} days`}
                icon="ph-users-three"
                colorClass="text-warning"
                bgClass="card-light-warning"
              />
            </div>
          </div>
        )}

        {/* ── Tab nav + content ── */}
        {data && (
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
              <ul className="nav nav-tabs tab-light-primary border-0" role="tablist">
                {[
                  { key: "summary", label: "Day-wise Summary",    icon: "ph-calendar-blank" },
                  { key: "members", label: "By Member",           icon: "ph-users-three"    },
                  { key: "detail",  label: "All Transactions",    icon: "ph-list-dashes"    },
                ].map(({ key, label, icon }) => (
                  <li className="nav-item" key={key}>
                    <button
                      className={`nav-link${activeTab === key ? " active" : ""}`}
                      onClick={() => setActiveTab(key)}
                    >
                      <i className={`ph-bold ${icon} pe-1`}></i>{label}
                    </button>
                  </li>
                ))}
              </ul>
              <span className="text-secondary f-s-12">
                {data.role} · {data.filters?.start_date} — {data.filters?.end_date}
              </span>
            </div>

            <div className="card-body p-0">

              {/* ── Day-wise Summary Tab ── */}
              {activeTab === "summary" && (
                <div className="table-responsive">
                  {summary.length === 0 ? (
                    <div className="text-center py-5 text-secondary">
                      <i className="ph-duotone ph-calendar-x f-s-48 d-block mb-2"></i>
                      No fund transactions found for this period
                    </div>
                  ) : (
                    <table className="table table-hover mb-0">
                      <thead style={{ background: "#f8f9fc" }}>
                        <tr>
                          <th className="f-s-12 f-w-700 text-secondary">Date</th>
                          <th className="f-s-12 f-w-700 text-secondary">Member</th>
                          <th className="f-s-12 f-w-700 text-secondary">Role</th>
                          <th className="f-s-12 f-w-700 text-secondary">Opening Bal</th>
                          <th className="f-s-12 f-w-700 text-success">Credit Received</th>
                          <th className="f-s-12 f-w-700 text-danger">Debit Transferred</th>
                          <th className="f-s-12 f-w-700 text-secondary">Net</th>
                          <th className="f-s-12 f-w-700 text-primary">Closing Bal</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.map((row, i) => {
                          const key = `${row.txn_date}-${row.member_id}`;
                          return (
                            <DayRow
                              key={key}
                              row={row}
                              isOpen={expandedKey === key}
                              onToggle={() => setExpandedKey(expandedKey === key ? null : key)}
                            />
                          );
                        })}
                      </tbody>
                      {/* Footer totals */}
                      <tfoot style={{ background: "#eef2ff", borderTop: "2px solid #c7d2fe" }}>
                        <tr>
                          <td colSpan={3} className="f-s-12 f-w-700 text-primary">TOTAL</td>
                          <td className="f-s-12 text-secondary">—</td>
                          <td className="f-s-13 f-w-700 text-success">{inr(g.total_credit)}</td>
                          <td className="f-s-13 f-w-700 text-danger">{inr(g.total_debit)}</td>
                          <td className={`f-s-13 f-w-700 ${(g.total_credit - g.total_debit) >= 0 ? "text-success" : "text-danger"}`}>
                            {inr(g.total_credit - g.total_debit)}
                          </td>
                          <td className="f-s-12 text-secondary">—</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              )}

              {/* ── By Member Tab ── */}
              {activeTab === "members" && (
                <div className="table-responsive">
                  {members.length === 0 ? (
                    <div className="text-center py-5 text-secondary">
                      <i className="ph-duotone ph-users f-s-48 d-block mb-2"></i>No member data
                    </div>
                  ) : (
                    <table className="table table-hover mb-0">
                      <thead style={{ background: "#f8f9fc" }}>
                        <tr>
                          <th className="f-s-12 f-w-700 text-secondary">Member</th>
                          <th className="f-s-12 f-w-700 text-secondary">Role</th>
                          <th className="f-s-12 f-w-700 text-success">Total Credit</th>
                          <th className="f-s-12 f-w-700 text-danger">Total Debit</th>
                          <th className="f-s-12 f-w-700 text-secondary">Net</th>
                          <th className="f-s-12 f-w-700 text-secondary">Txns</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((m, i) => (
                          <tr key={i}>
                            <td>
                              <div className="f-w-600 f-s-14">{m.member_name || "—"}</div>
                              <div className="text-secondary f-s-12">{m.member_phone}</div>
                            </td>
                            <td>
                              <span className="badge text-light-secondary f-s-11">{m.member_role}</span>
                            </td>
                            <td className="text-success f-w-700">{inr(m.total_credit)}</td>
                            <td className="text-danger f-w-700">{inr(m.total_debit)}</td>
                            <td>
                              <span className={`f-w-700 ${m.net >= 0 ? "text-success" : "text-danger"}`}>
                                {m.net >= 0 ? "+" : ""}{inr(m.net)}
                              </span>
                            </td>
                            <td>
                              <span className="badge text-light-primary">{m.total_txns}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot style={{ background: "#eef2ff", borderTop: "2px solid #c7d2fe" }}>
                        <tr>
                          <td colSpan={2} className="f-s-12 f-w-700 text-primary">TOTAL ({members.length} members)</td>
                          <td className="f-s-13 f-w-700 text-success">{inr(g.total_credit)}</td>
                          <td className="f-s-13 f-w-700 text-danger">{inr(g.total_debit)}</td>
                          <td className={`f-s-13 f-w-700 ${(g.total_credit - g.total_debit) >= 0 ? "text-success" : "text-danger"}`}>
                            {inr(g.total_credit - g.total_debit)}
                          </td>
                          <td className="f-s-12 f-w-700 text-primary">{g.total_txns}</td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              )}

              {/* ── All Transactions Tab ── */}
              {activeTab === "detail" && (
                <>
                  <div className="table-responsive">
                    {detail.length === 0 ? (
                      <div className="text-center py-5 text-secondary">
                        <i className="ph-duotone ph-receipt f-s-48 d-block mb-2"></i>No transactions found
                      </div>
                    ) : (
                      <table className="table table-hover mb-0">
                        <thead style={{ background: "#f8f9fc" }}>
                          <tr>
                            <th className="f-s-12 f-w-700 text-secondary">Date & Time</th>
                            <th className="f-s-12 f-w-700 text-secondary">Member</th>
                            <th className="f-s-12 f-w-700 text-secondary">Type</th>
                            <th className="f-s-12 f-w-700 text-secondary">Amount</th>
                            <th className="f-s-12 f-w-700 text-secondary">UTR / Ref</th>
                            <th className="f-s-12 f-w-700 text-secondary">Opening</th>
                            <th className="f-s-12 f-w-700 text-secondary">Closing</th>
                            <th className="f-s-12 f-w-700 text-secondary">Remark</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailPage.map((t, i) => (
                            <tr key={i}>
                              <td>
                                <div className="f-s-13 f-w-500">{t.txn_date}</div>
                                <div className="text-secondary f-s-11">{t.txn_datetime?.split(" ").slice(1).join(" ")}</div>
                              </td>
                              <td>
                                <div className="f-w-600 f-s-13">{t.member_name || "—"}</div>
                                <div className="text-secondary f-s-11">{t.member_phone}</div>
                              </td>
                              <td>
                                <span className={`badge ${t.flow === "credit" ? "text-light-success" : "text-light-danger"} f-s-11`}>
                                  <i className={`ph-fill ${FLOW_ICONS[t.flow]} me-1`}></i>
                                  {t.flow?.toUpperCase()}
                                </span>
                                <div className="text-secondary f-s-11 mt-1">{t.flow_label}</div>
                              </td>
                              <td>
                                <span className={`f-w-700 f-s-14 ${t.flow === "credit" ? "text-success" : "text-danger"}`}>
                                  {t.flow === "credit" ? "+" : "-"}{inr(t.amount)}
                                </span>
                              </td>
                              <td>
                                {t.utr ? (
                                  <span className="f-s-12 f-w-600 text-primary">
                                    <i className="ph ph-hash me-1"></i>{t.utr}
                                  </span>
                                ) : (
                                  <span className="text-secondary f-s-12">—</span>
                                )}
                                {t.ref_id && t.ref_id !== t.utr && (
                                  <div className="text-secondary f-s-11">{t.ref_id}</div>
                                )}
                              </td>
                              <td className="text-secondary f-s-12">{inr(t.opening_balance)}</td>
                              <td className="text-primary f-w-600 f-s-12">{inr(t.closing_balance)}</td>
                              <td className="text-secondary f-s-12" style={{ maxWidth: 120 }}>
                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {t.remark || "—"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pagination */}
                  {detail.length > PAGE_SIZE && (
                    <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
                      <span className="text-secondary f-s-13">
                        Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, detail.length)} of {detail.length}
                      </span>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          disabled={page === 1}
                          onClick={() => setPage(p => p - 1)}
                        >
                          <i className="ph-bold ph-caret-left"></i>
                        </button>
                        {Array.from({ length: Math.min(Math.ceil(detail.length / PAGE_SIZE), 7) }, (_, i) => {
                          const pg = Math.max(1, page - 3) + i;
                          if (pg > Math.ceil(detail.length / PAGE_SIZE)) return null;
                          return (
                            <button
                              key={pg}
                              className={`btn btn-sm ${pg === page ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => setPage(pg)}
                            >{pg}</button>
                          );
                        })}
                        <button
                          className="btn btn-sm btn-outline-primary"
                          disabled={page * PAGE_SIZE >= detail.length}
                          onClick={() => setPage(p => p + 1)}
                        >
                          <i className="ph-bold ph-caret-right"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        )}

        {/* ── Empty state before first fetch ── */}
        {!data && !loading && (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ph-duotone ph-chart-line-up f-s-64 text-primary d-block mb-3" style={{ opacity: 0.4 }}></i>
              <h5 className="text-secondary f-w-500">Select a date range and click Fetch Report</h5>
              <p className="text-secondary f-s-13">
                Shows opening balance, total credit, total debit, and closing balance per member per day —
                covering all fund transfers and credit/debit transactions.
              </p>
            </div>
          </div>
        )}

      </div>
    </main>
  );
}

export default FundSummaryReportPage;